import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import translationEN from './locales/en.json';
import translationSQ from './locales/sq.json';

const resources = {
  en: {
    translation: translationEN
  },
  sq: {
    translation: translationSQ
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // Fallback to the most-complete catalogue (English). If a key is missing
    // in `sq` we want users to see the English string, not the raw key.
    // The language comes from the detector below (no fixed `lng`, which
    // would switch detection off and ignore ?lang= and the visitor's
    // saved choice); the store's `default_locale` then applies to
    // visitors who haven't chosen (App.js LocaleSync).
    fallbackLng: 'en',
    supportedLngs: ['en', 'sq'],
    debug: false,

    interpolation: {
      escapeValue: false
    },

    // Detection order matters for SEO:
    //   1. ?lang=… in the URL is authoritative when present. Google
    //      crawls hreflang alternates as ?lang=en / ?lang=sq, and
    //      without this entry both URLs would render the same default
    //      language — Google would see duplicate content and the
    //      hreflang signal would be wasted.
    //   2. localStorage so the UI is sticky for returning visitors who
    //      switched language manually via LanguageSwitcher.
    //   3. navigator falls back to the browser locale on first visit.
    // Detected language is cached to localStorage on every detection so
    // a query-string visit also makes the choice sticky for follow-ups.
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      caches: ['localStorage'],
      convertDetectedLanguage: (lng) => String(lng).split('-')[0], // en-US → en
    }
  });

// Locales the storefront actually ships. Kept aligned with the
// backend `ALLOWED_LOCALES` allowlist in TranslationController.
const SUPPORTED_LOCALES = ['en', 'sq'];

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

/**
 * Convert a flat `{ "a.b.c": "v" }` map into a nested
 * `{ a: { b: { c: "v" } } }` shape that i18next's
 * addResourceBundle understands when deep-merging.
 *
 * Skips empty / non-string values defensively so a malformed row
 * in the DB can't blow up the whole catalogue.
 */
function unflatten(flat) {
  const out = {};
  if (!flat || typeof flat !== 'object') return out;

  for (const dottedKey of Object.keys(flat)) {
    const value = flat[dottedKey];
    if (typeof value !== 'string' || !dottedKey) continue;

    const segments = dottedKey.split('.');
    let cursor = out;
    for (let i = 0; i < segments.length - 1; i += 1) {
      const seg = segments[i];
      if (typeof cursor[seg] !== 'object' || cursor[seg] === null) {
        cursor[seg] = {};
      }
      cursor = cursor[seg];
    }
    cursor[segments[segments.length - 1]] = value;
  }

  return out;
}

/**
 * Fetch admin-managed overrides for a locale and layer them on top
 * of the bundled JSON resources. Fire-and-forget per locale; the
 * storefront renders bundled copy immediately and then "settles"
 * to the override copy once the response arrives.
 *
 * Failure modes (network down, 5xx, malformed payload) are swallowed
 * with a console warning — bundled copy is always a safe fallback,
 * and a broken /api/translations endpoint must NEVER take the
 * storefront down with it.
 */
async function applyOverridesForLocale(locale) {
  try {
    const res = await fetch(`${API_BASE_URL}/api/translations/${locale}`, {
      headers: { Accept: 'application/json' },
      credentials: 'omit',
    });
    if (!res.ok) return;

    const payload = await res.json();
    const flat = payload && payload.overrides;
    if (!flat || typeof flat !== 'object') return;
    if (Object.keys(flat).length === 0) return;

    // deep = true → merge into the bundled tree instead of
    //   replacing it wholesale (we only want to override the
    //   specific keys the operator changed).
    // overwrite = true → operator-managed copy wins over bundled.
    i18n.addResourceBundle(locale, 'translation', unflatten(flat), true, true);
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
       
      console.warn(`[i18n] failed to load overrides for ${locale}:`, err);
    }
  }
}

/**
 * Public entry point so the AdminTranslations page can refresh
 * the live catalogue immediately after a save (no full reload).
 */
export function refreshTranslationOverrides() {
  // Tests exercise bundled locale resources and mock only the API calls they
  // own. Avoid background localhost requests that make Jest noisy and flaky.
  if (process.env.NODE_ENV === 'test') return Promise.resolve([]);
  return Promise.all(SUPPORTED_LOCALES.map(applyOverridesForLocale));
}

// Kick off the initial fetch eagerly. We intentionally do NOT
// await it — the user sees bundled copy first, then the override
// settles in. For a small overrides set the swap is imperceptible.
refreshTranslationOverrides();

export default i18n;
